import React, { useState, useEffect } from 'react';
import { Product, Feature, Component, TestCase, Platform, PlatformItem } from '../../types';
import { apiRequest } from '../../utils/api';
import { useToast, showTempMessage } from '../../utils/messages';
import type { AppMessage } from '../../utils/messages';
import { Toast } from '../../utils/messages';
import { useProduct } from '../../contexts/ProductContext';
import FeatureImporter from './FeatureImporter';
import FeatureCard from './FeatureCard';
import AddTestCaseModal from './AddTestCaseModal';
import CreateFeatureModal from './CreateFeatureModal';
import FeatureViewModal from './FeatureViewModal';
import TestCaseReviewModal from './TestCaseReviewModal';
import ConfirmDialog from '../shared/ConfirmDialog';
import SearchFilterBar from '../shared/SearchFilterBar';
import MilestoneSelector from '../shared/MilestoneSelector';
import '../../App.css';

// Theme is applied globally via CSS

const FeaturesManagement: React.FC = () => {
  const { selectedProduct, products, setSelectedProduct, refreshProducts } = useProduct();
  const selectedProductId = selectedProduct?.id || '';
  const [features, setFeatures] = useState<Feature[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewingFeature, setViewingFeature] = useState<Feature | null>(null);
  const [showNewProductForm, setShowNewProductForm] = useState(false);
  const [newProductForm, setNewProductForm] = useState<{ name: string; projectId: string }>({ name: '', projectId: '' });
  const { message, setMessage, showMessage } = useToast();
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddTestCaseModal, setShowAddTestCaseModal] = useState(false);
  const [selectedComponentForTestCase, setSelectedComponentForTestCase] = useState<string | null>(null);
  const [selectedFeatureForTestCase, setSelectedFeatureForTestCase] = useState<string | null>(null);
  const [editingTestCase, setEditingTestCase] = useState<TestCase | null>(null);
  const [showCreateFeatureModal, setShowCreateFeatureModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState<Feature | null>(null);
  const [showCreateComponentModal, setShowCreateComponentModal] = useState(false);
  const [componentModalFeatureId, setComponentModalFeatureId] = useState<string | null>(null);
  const [componentModalComponentId, setComponentModalComponentId] = useState<string | null>(null); // For editing
  const [componentModalName, setComponentModalName] = useState('');
  const [componentModalDescription, setComponentModalDescription] = useState('');
  const [componentModalPlatforms, setComponentModalPlatforms] = useState<Platform[]>([]);
  const [componentModalMilestone, setComponentModalMilestone] = useState<string>('');
  const [availablePlatforms, setAvailablePlatforms] = useState<PlatformItem[]>([]);
  const [draggedTestCase, setDraggedTestCase] = useState<string | null>(null);
  const [dragOverComponent, setDragOverComponent] = useState<string | null>(null);
  const [confirmDeleteComponent, setConfirmDeleteComponent] = useState<{ componentId: string; componentName: string } | null>(null);
  const [confirmDeleteFeature, setConfirmDeleteFeature] = useState<{ featureId: string; featureName: string } | null>(null);
  const [confirmDeleteTestCase, setConfirmDeleteTestCase] = useState<{ testCaseId: string; testCaseTitle: string } | null>(null);
  const [filterText, setFilterText] = useState('');
  const [viewingTestCase, setViewingTestCase] = useState<TestCase | null>(null);

  // Load initial data on mount and when product changes
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Fetch platforms
        const platformsData = await apiRequest<PlatformItem[]>('/api/platforms', {}, { useAuth: true });
        
        // Filter platforms by selected product
        // Show platforms that either:
        // 1. Have no productIds (applies to all products), OR
        // 2. Include the selected productId in their productIds array
        const allPlatforms = Array.isArray(platformsData) ? platformsData : [];
        const filteredPlatforms = selectedProductId
          ? allPlatforms.filter(p => 
              !p.productIds || 
              p.productIds.length === 0 || 
              p.productIds.includes(selectedProductId)
            )
          : allPlatforms; // If no product selected, show all platforms
        
        setAvailablePlatforms(filteredPlatforms);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, [selectedProduct, selectedProductId]);

  // Fetch features when product changes
  useEffect(() => {
    if (!selectedProductId) {
      setFeatures([]);
      setComponents([]);
      setTestCases([]);
      return;
    }
    
    const fetchFeatures = async () => {
      try {
        const data = await apiRequest<Feature[]>(`/api/features?productId=${selectedProductId}`, {}, { useAuth: true });
        setFeatures(data);
      } catch (error) {
        console.error('Error fetching features:', error);
      }
    };
    fetchFeatures();
  }, [selectedProductId]);

  // Fetch components when features change
  useEffect(() => {
    if (features.length === 0) {
      setComponents([]);
      setTestCases([]);
      return;
    }
    
    const fetchComponents = async () => {
      try {
        const componentsData = await apiRequest<Component[]>('/api/components', {}, { useAuth: true }).catch(() => []);
        
        if (Array.isArray(componentsData)) {
          const featureIds = features.map(f => f.id);
          const filtered = componentsData.filter((c: Component) => featureIds.includes(c.featureId));
          setComponents(filtered);
        }
      } catch (error) {
        console.error('Error fetching components:', error);
        setComponents([]);
      }
    };
    fetchComponents();
  }, [features]);

  // Fetch test cases when components or features change
  useEffect(() => {
    if (features.length === 0) {
      setTestCases([]);
      return;
    }
    
    const fetchTestCases = async () => {
      try {
        const testCasesData = await apiRequest<TestCase[]>('/api/testcases', {}, { useAuth: true }).catch(() => []);
        
        if (Array.isArray(testCasesData)) {
          const componentIds = components.map(c => c.id);
          const featureIds = features.map(f => f.id);
          // Include test cases that belong to components OR directly to features
          const filtered = testCasesData.filter((tc: TestCase) => 
            (tc.componentId && componentIds.includes(tc.componentId)) ||
            (!tc.componentId && tc.featureId && featureIds.includes(tc.featureId))
          );
          setTestCases(filtered);
        }
      } catch (error) {
        console.error('Error fetching test cases:', error);
        setTestCases([]);
      }
    };
    fetchTestCases();
  }, [components, features]);


  // Keep fetchAllComponents for re-fetching after create/import
  const fetchAllComponents = async () => {
    if (!selectedProductId) return;
    try {
      // Fetch features first to ensure we have the latest list
      const currentFeatures = await apiRequest<Feature[]>(`/api/features?productId=${selectedProductId}`, {}, { useAuth: true }).catch(() => []);
      const featuresToUse = Array.isArray(currentFeatures) && currentFeatures.length > 0 ? currentFeatures : features;
      
      const [componentsData, testCasesData] = await Promise.all([
        apiRequest<Component[]>('/api/components', {}, { useAuth: true }).catch(() => []),
        apiRequest<TestCase[]>('/api/testcases', {}, { useAuth: true }).catch(() => [])
      ]);
      
      let filteredComponents: Component[] = [];
      if (Array.isArray(componentsData)) {
        const featureIds = featuresToUse.map(f => f.id);
        filteredComponents = componentsData.filter((c: Component) => featureIds.includes(c.featureId));
        setComponents(filteredComponents);
      }
      
      if (Array.isArray(testCasesData)) {
        const componentIds = filteredComponents.map(c => c.id);
        const featureIds = featuresToUse.map(f => f.id);
        // Include test cases that belong to components OR directly to features
        const filtered = testCasesData.filter((tc: TestCase) => 
          (tc.componentId && componentIds.includes(tc.componentId)) ||
          (!tc.componentId && tc.featureId && featureIds.includes(tc.featureId))
        );
        setTestCases(filtered);
      }
    } catch (error) {
      console.error('Error fetching components/test cases:', error);
    }
  };

  const handleCreateProduct = async () => {
    if (!newProductForm.name.trim()) {
      showTempMessage(setMessage, { type: 'error', text: 'Project name is required' });
      return;
    }
    if (!newProductForm.projectId.trim() || newProductForm.projectId.length < 2) {
      showTempMessage(setMessage, { type: 'error', text: 'Project ID is required (minimum 2 characters)' });
      return;
    }

    try {
      const newProduct = await apiRequest<Product>('/api/products', {
        method: 'POST',
        body: JSON.stringify({ 
          name: newProductForm.name.trim(), 
          projectId: newProductForm.projectId.trim() 
        })
      }, { 
        useAuth: true,
        setMessage,
        successMessage: 'Product created successfully'
      });
      await refreshProducts();
      setSelectedProduct(newProduct);
      setShowNewProductForm(false);
      setNewProductForm({ name: '', projectId: '' });
    } catch (error) {
      console.error('Error creating product:', error);
    }
  };

  const handleImportComplete = async () => {
    // Fetch features first to get the latest list (including any newly imported)
    const updatedFeatures = await apiRequest<Feature[]>(`/api/features?productId=${selectedProductId}`, {}, { useAuth: true });
    setFeatures(updatedFeatures || []);
    // Now fetch components which will use the updated features
    await fetchAllComponents();
  };

  const handleFeatureCreated = async (feature: Feature) => {
    // Optimistically update UI instead of refetching everything
    setFeatures(prev => [...prev, feature]);
    // Open the modal to view the new feature
    setViewingFeature(feature);
  };

  const handleCreateComponent = async (featureId: string): Promise<void> => {
    setComponentModalFeatureId(featureId);
    setComponentModalComponentId(null); // Clear edit mode
    setComponentModalName('');
    setComponentModalDescription('');
    setComponentModalPlatforms([]);
    setComponentModalMilestone('');
    setShowCreateComponentModal(true);
  };

  const handleEditComponent = async (component: Component): Promise<void> => {
    setComponentModalFeatureId(component.featureId);
    setComponentModalComponentId(component.id); // Set edit mode
    setComponentModalName(component.name);
    setComponentModalDescription(component.description || '');
    setComponentModalPlatforms(component.platforms || []);
    setComponentModalMilestone(component.milestone || '');
    setShowCreateComponentModal(true);
  };

  const handleSubmitComponent = async () => {
    if (!componentModalName.trim()) {
      if (setMessage) {
        showTempMessage(setMessage, { type: 'error', text: 'Component name is required' });
      }
      return;
    }

    if (!componentModalFeatureId) {
      if (setMessage) {
        showTempMessage(setMessage, { type: 'error', text: 'Feature ID is required' });
      }
      return;
    }

    if (!componentModalMilestone || !componentModalMilestone.trim()) {
      if (setMessage) {
        showTempMessage(setMessage, { type: 'error', text: 'Milestone is required' });
      }
      return;
    }

    try {
      const isEditing = componentModalComponentId !== null;
      const componentData = {
        name: componentModalName.trim(),
        featureId: componentModalFeatureId,
        description: componentModalDescription.trim() || undefined,
        platforms: componentModalPlatforms.length > 0 ? componentModalPlatforms : undefined,
        milestone: componentModalMilestone.trim()
      };

      let updatedComponent: Component;
      if (isEditing) {
        updatedComponent = await apiRequest<Component>(`/api/components/${componentModalComponentId}`, {
          method: 'PUT',
          body: JSON.stringify(componentData)
        }, {
          useAuth: true,
          setMessage,
          successMessage: 'Component updated successfully'
        });
        // Update local state
        setComponents(prev => prev.map(c => c.id === componentModalComponentId ? updatedComponent : c));
      } else {
        updatedComponent = await apiRequest<Component>('/api/components', {
        method: 'POST',
          body: JSON.stringify(componentData)
      }, {
        useAuth: true,
        setMessage,
          successMessage: 'Component created successfully'
      });
      // Optimistically update UI instead of refetching everything
        setComponents(prev => [...prev, updatedComponent]);
      }
      
      setShowCreateComponentModal(false);
      setComponentModalName('');
      setComponentModalDescription('');
      setComponentModalPlatforms([]);
      setComponentModalMilestone('');
      setComponentModalFeatureId(null);
      setComponentModalComponentId(null);
    } catch (error) {
      console.error(`Error ${componentModalComponentId ? 'updating' : 'creating'} component:`, error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      if (setMessage) {
        showTempMessage(setMessage, { 
          type: 'error', 
          text: `Failed to ${componentModalComponentId ? 'update' : 'create'} component: ${errorMsg}.` 
        });
      }
      // Refetch on error to ensure consistency
      await fetchAllComponents();
    }
  };

  const handleTestCaseAdded = async (newTestCase: TestCase) => {
    // Optimistically update UI
    setTestCases(prev => {
      // Check if it already exists (shouldn't happen, but just in case)
      if (prev.find(tc => tc.id === newTestCase.id)) {
        return prev;
    }
      return [...prev, newTestCase];
    });
    
    if (setMessage) {
      showTempMessage(setMessage, { type: 'success', text: 'Test case created successfully' });
    }
  };

  const handleTestCaseUpdated = async (updatedTestCase: TestCase) => {
    // Update the test case in the list
    setTestCases(prev => prev.map(tc => tc.id === updatedTestCase.id ? updatedTestCase : tc));
    
    if (setMessage) {
      showTempMessage(setMessage, { type: 'success', text: 'Test case updated successfully' });
    }
  };

  const handleEditTestCase = (testCaseId: string) => {
    const testCase = testCases.find(tc => tc.id === testCaseId);
    if (testCase) {
      setEditingTestCase(testCase);
      setShowAddTestCaseModal(true);
    }
  };

  const handleCreateTestCase = async (componentIdOrFeatureId: string, isFeature?: boolean): Promise<void> => {
    if (isFeature) {
      setSelectedComponentForTestCase(null);
      setSelectedFeatureForTestCase(componentIdOrFeatureId);
    } else {
      setSelectedComponentForTestCase(componentIdOrFeatureId);
      setSelectedFeatureForTestCase(null);
    }
    setShowAddTestCaseModal(true);
  };

  const handleUpdateTestCase = async (testCaseId: string, updates: Partial<TestCase>) => {
    try {
      const updated = await apiRequest<TestCase>(`/api/testcases/${testCaseId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      }, {
        useAuth: true,
        setMessage: undefined // Don't show message for every update (too noisy)
      });
      // Update local state
      setTestCases(prev => prev.map(tc => tc.id === testCaseId ? updated : tc));
    } catch (error) {
      console.error('Error updating test case:', error);
    }
  };

  const handleDeleteTestCase = async (testCaseId: string) => {
    const testCase = testCases.find(tc => tc.id === testCaseId);
    if (testCase) {
      setConfirmDeleteTestCase({ testCaseId, testCaseTitle: testCase.title });
    }
  };

  const confirmDeleteTestCaseAction = async () => {
    if (!confirmDeleteTestCase) return;

    const { testCaseId } = confirmDeleteTestCase;
    try {
      await apiRequest(`/api/testcases/${testCaseId}`, {
        method: 'DELETE'
      }, {
        useAuth: true,
        setMessage,
        successMessage: 'Test case deleted successfully'
      });
      setTestCases(testCases.filter(tc => tc.id !== testCaseId));
      setConfirmDeleteTestCase(null);
    } catch (error) {
      console.error('Error deleting test case:', error);
      setConfirmDeleteTestCase(null);
    }
  };

  // Drag and drop handlers for test cases
  const handleTestCaseDragStart = (e: React.DragEvent, testCaseId: string) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', testCaseId);
    setDraggedTestCase(testCaseId);
  };

  const handleTestCaseDragEnd = () => {
    setDraggedTestCase(null);
    setDragOverComponent(null);
  };

  const handleComponentDragOver = (e: React.DragEvent, componentId: string) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverComponent(componentId);
  };

  const handleComponentDragLeave = () => {
    setDragOverComponent(null);
  };

  const handleTestCaseDrop = async (e: React.DragEvent, targetComponentId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    const testCaseId = e.dataTransfer.getData('text/plain');
    if (!testCaseId || !draggedTestCase) return;

    const testCase = testCases.find(tc => tc.id === testCaseId);
    if (!testCase || testCase.componentId === targetComponentId) {
      setDraggedTestCase(null);
      setDragOverComponent(null);
      return;
    }

    try {
      await handleUpdateTestCase(testCaseId, { componentId: targetComponentId });
      if (setMessage) {
        showTempMessage(setMessage, { 
          type: 'success', 
          text: `Test case moved to ${components.find(c => c.id === targetComponentId)?.name || 'component'}` 
        });
      }
    } catch (error) {
      console.error('Error moving test case:', error);
      if (setMessage) {
        showTempMessage(setMessage, { type: 'error', text: 'Failed to move test case' });
      }
    } finally {
      setDraggedTestCase(null);
      setDragOverComponent(null);
    }
  };

  const handleDeleteFeature = async (featureId: string) => {
    const feature = features.find(f => f.id === featureId);
    if (feature) {
      setConfirmDeleteFeature({ featureId, featureName: feature.name });
    }
  };

  const confirmDeleteFeatureAction = async () => {
    if (!confirmDeleteFeature) return;

    const { featureId } = confirmDeleteFeature;
    try {
      await apiRequest(`/api/features/${featureId}`, {
        method: 'DELETE'
      }, {
        useAuth: true,
        setMessage,
        successMessage: 'Feature deleted successfully'
      });
      setFeatures(features.filter(f => f.id !== featureId));
      // Close the feature view modal if it's open for this feature
      if (viewingFeature?.id === featureId) {
        setViewingFeature(null);
      }
      setConfirmDeleteFeature(null);
    } catch (error) {
      console.error('Error deleting feature:', error);
      setConfirmDeleteFeature(null);
    }
  };

  const handleUpdateComponent = async (componentId: string, updates: Partial<Component>) => {
    try {
      const updated = await apiRequest<Component>(`/api/components/${componentId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      }, {
        useAuth: true,
        setMessage: undefined // Don't show message for every update (too noisy)
      });
      // Update local state
      setComponents(prev => prev.map(c => c.id === componentId ? updated : c));
    } catch (error) {
      console.error('Error updating component:', error);
    }
  };

  const handleDeleteComponent = async (componentId: string) => {
    const component = components.find(c => c.id === componentId);
    if (component) {
      setConfirmDeleteComponent({ componentId, componentName: component.name });
    }
  };

  const confirmDeleteComponentAction = async () => {
    if (!confirmDeleteComponent) return;

    const { componentId } = confirmDeleteComponent;
    try {
      await apiRequest(`/api/components/${componentId}`, {
        method: 'DELETE'
      }, {
        useAuth: true,
        setMessage,
        successMessage: 'Component deleted successfully'
      });
      setComponents(components.filter(c => c.id !== componentId));
      // Also remove test cases for this component
      setTestCases(testCases.filter(tc => tc.componentId !== componentId));
      setConfirmDeleteComponent(null);
    } catch (error) {
      console.error('Error deleting component:', error);
      setConfirmDeleteComponent(null);
    }
  };





  if (loading) {
    return <div className="form-container">Loading...</div>;
  }

  const selectedProductName = selectedProduct?.name || '';

  return (
    <div className="form-container">
      <Toast message={message} />

      {/* New Product Form Modal */}
      {showNewProductForm && (
        <div className="modal-overlay" onClick={() => setShowNewProductForm(false)}>
          <div className="card modal-content" onClick={e => e.stopPropagation()}>
            <h2 className="title-lg mb-md">Create New Product</h2>
            <div className="flex flex-col gap-md">
              <div>
                <label className="label">Product Name *</label>
                <input
                  type="text"
                  value={newProductForm.name}
                  onChange={(e) => setNewProductForm({ ...newProductForm, name: e.target.value })}
                  className="input"
                  placeholder="e.g., My Application"
                  autoFocus
                />
              </div>
              <div>
                <label className="label">Project ID *</label>
                <input
                  type="text"
                  value={newProductForm.projectId}
                  onChange={(e) => setNewProductForm({ ...newProductForm, projectId: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                  className="input uppercase"
                  placeholder="e.g., FS, ATAK, APP"
                  maxLength={10}
                />
                <p className="hint mt-sm">2-10 uppercase letters/numbers. Used as prefix for session IDs (e.g., "FS" → "FS-1")</p>
              </div>
              <div className="flex gap-sm mt-md">
                <button className="btn btn-primary" onClick={handleCreateProduct}>
                  Create Product
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowNewProductForm(false);
                    setNewProductForm({ name: '', projectId: '' });
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedProductId && (
        <>
          {/* Header Section */}
          <div className="page-header" style={{ marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: '2px solid var(--grid-line)' }}>
            <div className="page-header-content" style={{ flex: 1 }}>
              <h1 className="page-title" style={{ marginBottom: '0.5rem' }}>Manage {selectedProductName} Features</h1>
              <p className="subtitle" style={{ marginBottom: '0.5rem', fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
                {features.length} feature{features.length !== 1 ? 's' : ''} • {components.length} component{components.length !== 1 ? 's' : ''} • {testCases.length} test case{testCases.length !== 1 ? 's' : ''}
              </p>
              <p className="page-subtitle" style={{ margin: 0 }}>
                Organize features components and test cases for your testing sessions
              </p>
            </div>
            <div className="flex gap-sm" style={{ marginLeft: '2rem', flexShrink: 0 }}>
              <button 
                className="btn btn-primary" 
                onClick={() => setShowImportModal(true)}
                disabled={!selectedProductId}
              >
                Import Features
              </button>
              <button className="btn btn-primary" onClick={() => setShowCreateFeatureModal(true)}>
                Add New Feature
              </button>
            </div>
          </div>

          {/* Feature View Modal */}
          {viewingFeature && (
            <FeatureViewModal
              feature={viewingFeature}
              components={components}
              testCases={testCases}
              dragOverComponent={dragOverComponent}
              draggedTestCase={draggedTestCase}
              onClose={() => setViewingFeature(null)}
              onDelete={handleDeleteFeature}
              onEdit={(feature) => {
                setEditingFeature(feature);
                setShowCreateFeatureModal(true);
              }}
              onCreateComponent={handleCreateComponent}
              onEditComponent={handleEditComponent}
              onDeleteComponent={handleDeleteComponent}
              onUpdateComponent={handleUpdateComponent}
              onCreateTestCase={handleCreateTestCase}
              onDeleteTestCase={handleDeleteTestCase}
              onUpdateTestCase={handleUpdateTestCase}
              onTestCaseDragStart={handleTestCaseDragStart}
              onTestCaseDragEnd={handleTestCaseDragEnd}
              onComponentDragOver={handleComponentDragOver}
              onComponentDragLeave={handleComponentDragLeave}
              onTestCaseDrop={handleTestCaseDrop}
              onEditTestCase={handleEditTestCase}
              onViewTestCase={(testCase) => setViewingTestCase(testCase)}
              setMessage={setMessage}
              platforms={availablePlatforms}
              productId={selectedProductId}
              projectCode={selectedProduct?.projectId}
              onComponentsUpdated={async () => {
                // Refresh components after bulk edit
                const updatedComponents = await apiRequest<Component[]>('/api/components', {}, { useAuth: true });
                setComponents(updatedComponents);
              }}
            />
          )}

          {/* Import Modal */}
          <FeatureImporter
            selectedProduct={selectedProductId}
            products={products}
            features={features}
            onImportComplete={handleImportComplete}
            setMessage={setMessage}
            isOpen={showImportModal}
            onClose={() => setShowImportModal(false)}
          />

          {/* Filter Controls */}
          <SearchFilterBar
            context="features"
            filterText={filterText}
            onFilterTextChange={setFilterText}
            showSort={false}
          />

          {/* Features List */}
          {(() => {
            // Filter features
            const filterFeatures = (featuresList: Feature[]): Feature[] => {
              if (!filterText.trim()) return featuresList;

              const filter = filterText.toLowerCase();
              return featuresList.filter(feature => {
                // Search in feature name, description
                const matchesFeature = 
                  feature.name.toLowerCase().includes(filter) ||
                  (feature.description && feature.description.toLowerCase().includes(filter));

                // Search in components
                const featureComponents = components.filter(c => c.featureId === feature.id);
                const matchesComponent = featureComponents.some(comp =>
                  comp.name.toLowerCase().includes(filter) ||
                  (comp.description && comp.description.toLowerCase().includes(filter))
                );

                // Search in test cases
                const featureTestCases = testCases.filter(tc => 
                  tc.featureId === feature.id || featureComponents.some(c => c.id === tc.componentId)
                );
                const matchesTestCase = featureTestCases.some(tc =>
                  tc.title.toLowerCase().includes(filter) ||
                  (tc.description && tc.description.toLowerCase().includes(filter)) ||
                  (tc.qaseId && tc.qaseId.toLowerCase().includes(filter))
                );

                return matchesFeature || matchesComponent || matchesTestCase;
              });
            };

            const displayedFeatures = filterFeatures(features);

            return displayedFeatures.length === 0 ? (
              <div className="card empty-state empty-state-dashed">
                <div className="empty-state-icon">📋</div>
                <h3 className="text-secondary mb-sm">
                  {filterText ? 'No features match your filter' : 'No features yet'}
                </h3>
                <p className="text-muted mb-md">
                  {filterText 
                    ? 'Try adjusting your search criteria.'
                    : 'Use the Import section above to import features and sub-dependencies.'}
                </p>
              </div>
            ) : (
              <div className="features-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                {displayedFeatures.map((feature) => {
                return (
                  <FeatureCard
                    key={feature.id}
                    feature={feature}
                    components={components}
                    testCases={testCases}
                    onToggleExpand={() => {
                      setViewingFeature(feature);
                    }}
                    onDelete={handleDeleteFeature}
                    onEdit={(feature) => {
                      setEditingFeature(feature);
                      setShowCreateFeatureModal(true);
                    }}
                  />
                );
              })}
            </div>
          );
          })()}
        </>
      )}

      {!selectedProductId && (
        <div className="card empty-state">
          <div className="empty-state-icon">📦</div>
          <h3 className="text-secondary mb-sm">Select a Product</h3>
          <p className="text-muted">
            Choose a product from the dropdown above to manage its features and components.
          </p>
        </div>
      )}

      {/* Add Test Case Modal */}
      <AddTestCaseModal
        allComponents={components}
        allFeatures={features}
        isOpen={showAddTestCaseModal}
        onClose={() => {
          setShowAddTestCaseModal(false);
          setSelectedComponentForTestCase(null);
          setSelectedFeatureForTestCase(null);
          setEditingTestCase(null);
        }}
        onTestCaseAdded={handleTestCaseAdded}
        onTestCaseUpdated={handleTestCaseUpdated}
        showMessage={(msg: AppMessage) => {
          if (setMessage) {
            showTempMessage(setMessage, msg);
          }
        }}
        prefillData={selectedComponentForTestCase ? { componentId: selectedComponentForTestCase } : selectedFeatureForTestCase ? { featureId: selectedFeatureForTestCase } : undefined}
        editingTestCase={editingTestCase}
      />

      {/* Create Component Modal */}
      {showCreateComponentModal && componentModalFeatureId && (
          <div className="modal-overlay" onClick={() => {
            setShowCreateComponentModal(false);
            setComponentModalFeatureId(null);
            setComponentModalComponentId(null);
            setComponentModalName('');
            setComponentModalDescription('');
            setComponentModalPlatforms([]);
            setComponentModalMilestone('');
          }}>
            <div className="card modal-content" onClick={(e) => e.stopPropagation()}>
              <h2 className="title-lg mb-md">{componentModalComponentId ? 'Edit Component' : 'Create Component'}</h2>
              
              <div className="mb-md">
                <label className="label">Component Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  placeholder="e.g., Auto Route"
                  value={componentModalName}
                  onChange={(e) => setComponentModalName(e.target.value)}
                  className="form-control"
                  onKeyPress={(e) => e.key === 'Enter' && handleSubmitComponent()}
                />
              </div>

              <div className="mb-md">
                <label className="label">Description (Optional)</label>
                <textarea
                  value={componentModalDescription}
                  onChange={(e) => setComponentModalDescription(e.target.value)}
                  className="form-control"
                  rows={3}
                  placeholder="Component description..."
                />
              </div>

              <div className="mb-md">
                <label className="label">Milestone <span className="text-danger">*</span></label>
                <MilestoneSelector
                  value={componentModalMilestone}
                  onChange={setComponentModalMilestone}
                  productId={selectedProductId}
                  placeholder="Select or create milestone"
                  required
                />
              </div>

            <div className="mb-md">
              <label className="label">Platforms</label>
              {availablePlatforms.length === 0 ? (
                <div style={{ fontSize: '0.875rem', color: '#999', fontStyle: 'italic' }}>
                  No platforms available. Please add platforms in Settings.
                </div>
              ) : (
                <>
                  <div className="platform-controls-group-runner">
                    {availablePlatforms.map(platformItem => {
                      const platform = platformItem.name as Platform;
                      const isSelected = componentModalPlatforms.includes(platform);
                      return (
                        <div key={platformItem.id} className="platform-control-wrapper-runner">
                          <button
                            type="button"
                            className={`platform-tested-button ${isSelected ? 'tested' : ''}`}
                            onClick={() => {
                              if (isSelected) {
                                setComponentModalPlatforms(componentModalPlatforms.filter(p => p !== platform));
                              } else {
                                setComponentModalPlatforms([...componentModalPlatforms, platform]);
                              }
                            }}
                            title={isSelected ? `${platform} selected (click to deselect)` : `Select ${platform}`}
                          >
                            <span className="platform-name">{platform}</span>
                            {isSelected && <span className="platform-icon">✓</span>}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                    Select platforms this component tests on. Used for insights and platform coverage metrics.
                  </div>
                </>
              )}
            </div>

              <div className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreateComponentModal(false);
                    setComponentModalFeatureId(null);
                    setComponentModalComponentId(null);
                    setComponentModalName('');
                    setComponentModalDescription('');
                    setComponentModalPlatforms([]);
                    setComponentModalMilestone('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSubmitComponent}
                  disabled={!componentModalName.trim() || !componentModalMilestone.trim()}
                >
                  {componentModalComponentId ? 'Update Component' : 'Create Component'}
                </button>
              </div>
            </div>
          </div>
      )}

      {/* Create/Edit Feature Modal */}
      {showCreateFeatureModal && (
        <CreateFeatureModal
          isOpen={showCreateFeatureModal}
          onClose={() => {
            setShowCreateFeatureModal(false);
            setEditingFeature(null);
          }}
          productId={selectedProductId}
          products={products}
          onFeatureCreated={handleFeatureCreated}
          setMessage={setMessage}
          showMessage={showMessage}
          editingFeature={editingFeature}
          onFeatureUpdated={(updatedFeature) => {
            setFeatures(prev => prev.map(f => f.id === updatedFeature.id ? updatedFeature : f));
            // Update viewingFeature if it's currently being viewed
            setViewingFeature(prev => prev?.id === updatedFeature.id ? updatedFeature : prev);
            setEditingFeature(null);
            setShowCreateFeatureModal(false);
          }}
        />
      )}

      {/* Component Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDeleteComponent !== null}
        title="Delete Component"
        message={`Are you sure you want to delete "${confirmDeleteComponent?.componentName}"? This will also delete all its test cases.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteComponentAction}
        onCancel={() => setConfirmDeleteComponent(null)}
        danger={true}
      />

      {/* Feature Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDeleteFeature !== null}
        title="Delete Feature"
        message={`Are you sure you want to delete "${confirmDeleteFeature?.featureName}"? This will also delete all its components and test cases.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteFeatureAction}
        onCancel={() => setConfirmDeleteFeature(null)}
        danger={true}
      />

      {/* Test Case Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDeleteTestCase !== null}
        title="Delete Test Case"
        message={`Are you sure you want to delete "${confirmDeleteTestCase?.testCaseTitle}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteTestCaseAction}
        onCancel={() => setConfirmDeleteTestCase(null)}
        danger={true}
      />

      {/* Test Case Review Modal */}
      {viewingTestCase && (
        <TestCaseReviewModal
          testCase={viewingTestCase}
          feature={(() => {
            // Find feature from component or directly from test case
            const component = components.find(c => c.id === viewingTestCase.componentId);
            if (component) {
              return features.find(f => f.id === component.featureId);
            }
            return features.find(f => f.id === viewingTestCase.featureId);
          })()}
          component={components.find(c => c.id === viewingTestCase.componentId)}
          projectCode={selectedProduct?.projectId}
          onClose={() => setViewingTestCase(null)}
          onEdit={(testCase) => {
            setViewingTestCase(null);
            setEditingTestCase(testCase);
            setShowAddTestCaseModal(true);
          }}
          onDelete={(testCaseId) => {
            handleDeleteTestCase(testCaseId);
            setViewingTestCase(null);
          }}
          zIndex={2000}
        />
      )}
    </div>
  );
};

export default FeaturesManagement;
